import React, { useMemo, useState } from 'react';
import { dig } from '@this/src/util';
import { Link } from '@this/shared/ui/navigations/link';
import ContentBody from '@this/components/shared/atoms/content_body';
import { getColor, useAITTheme, type AITTheme } from '@this/shared/ui/theme';

import AlertArea from '@this/components/reserve_trip/select/alert_area/alert_area';
import type SearchQuery from '../../../../domain/search_query';
import type SearchQueryItem from '../../../../domain/search_query_item';
import type { Airline } from '../../../../domain/select_store';
import type User from '../../../../domain/user/user';

import { useSelectContext } from '../context/SelectContext';

import SelectDefaultArea from '../default_area/default_area';
import ListAreaShortDistance from '../list_area/short_distance/ListAreaShortDistance';
import SelectSearchArea from '../search_area/search_area';
import OutlineWrapperShortDistanceContainer from '../outline_wrapper/OutlineWrapperShortDistanceContainer';
import TripPlanSelector from '../outline_wrapper/short_distance/TripPlanSelector';

import ArrangementRequestForm from '../arrangement_request_form/arrangement_request_form';

interface Props {
  // Props not in Context (remaining)
  showArrangementRequestForm: boolean;
  showSearchBox: boolean;
  additionalSearchErrors?: { [k in string]: string };
  serviceId: number;
  user: User | null;
  availableOptions: string[];

  // Handler props not in Context
  handleAirline: (airline: Airline) => () => void;
  handleSelectItem: () => void;
  handleAddItem: (item_type: 'transport' | 'hotel', e: React.MouseEvent<HTMLElement>) => void;
  handleAdditionalSearch: (queryItem: SearchQueryItem, e: React.FormEvent<HTMLFormElement>) => void;
  handleReSearch: (query: SearchQuery) => void;
  handleToggleArrangementRequestForm: () => void;
  toggleShowSearchBox: () => void;
  handleOpenShowSearchBox: () => void;
}

const SpShortDistance: React.FC<Props> = ({
  showArrangementRequestForm,
  serviceId,
  user,
  availableOptions,
  showSearchBox,
  additionalSearchErrors,

  handleAirline,
  handleSelectItem,
  handleAddItem,
  handleAdditionalSearch,
  handleReSearch,
  handleToggleArrangementRequestForm,
  toggleShowSearchBox,
  handleOpenShowSearchBox
}) => {
  // Get values from Context
  const {
    store,
    repository,
    query,
    selectErrors,
    submitting,
    isAnyItemLoading,
    handleSelectType,
    handleNextButtonClick
  } = useSelectContext();
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const isButtonDisabled = submitting || isAnyItemLoading;
  const [showTooltip, setShowTooltip] = useState(false);

  if (store == null || repository == null) {
    return null;
  }

  return (
    <div className="select-sp">
      {showArrangementRequestForm ? (
        <>
          <div style={styles.backHeader}>
            <Link onClick={handleToggleArrangementRequestForm} style={styles.backHeaderLink}>
              {'< 戻る'}
            </Link>
          </div>
          <ContentBody style={styles.body}>
            <ArrangementRequestForm
              serviceId={serviceId}
              query={query}
              store={store}
              selectErrors={selectErrors}
              handleSelectItem={handleSelectItem}
              handleAdditionalSearch={handleAdditionalSearch}
              additionalSearchErrors={additionalSearchErrors}
              handleNextButtonClick={handleNextButtonClick}
              handleOpenShowSearchBox={handleOpenShowSearchBox}
              reserveRequestMailNotificationType={
                store.user?.organization?.reserve_request_mail_notification_type ?? 'togglable'
              }
              shortdistance
            />
          </ContentBody>
        </>
      ) : (
        <>
          {store.currentTab === 'none' && (
            <ContentBody style={styles.body}>
              <SelectSearchArea
                query={query}
                user={user}
                availableOptions={availableOptions}
                trip={store.result.getReservingTrip()}
                selectErrors={selectErrors}
                submitting={isButtonDisabled}
                store={store}
                handleNextButtonClick={handleNextButtonClick}
                handleReSearch={handleReSearch}
              />
              <AlertArea result={store.result} />
              <div style={{ paddingTop: '20px' }}>
                <TripPlanSelector
                  store={store}
                  handleSelectType={handleSelectType}
                  handleAirline={handleAirline}
                  width={100}
                  serviceId={serviceId}
                />
              </div>
              <OutlineWrapperShortDistanceContainer
                query={query}
                store={store}
                selectErrors={selectErrors}
                packageErrors={store.packageErrors[store.result.airline]}
                handleSelectItem={handleSelectItem}
                handleAddItem={handleAddItem}
                handleAdditionalSearch={handleAdditionalSearch}
                additionalSearchErrors={additionalSearchErrors}
                handleNextButtonClick={handleNextButtonClick}
                submitting={isButtonDisabled}
                handleOpenShowSearchBox={handleOpenShowSearchBox}
              />
              {dig(store, 'user', 'organization', 'arrangement_request_enable?') ? (
                <div style={styles.requestText}>
                  <p>
                    お探しの結果が見つからない場合は
                    <Link onClick={handleToggleArrangementRequestForm} style={styles.requestFormLink}>
                      リクエストフォーム
                    </Link>
                    からご依頼ください
                  </p>
                  <div style={styles.tooltipContainer}>
                    <div
                      style={styles.helpButton}
                      onMouseEnter={() => setShowTooltip(true)}
                      onMouseLeave={() => setShowTooltip(false)}
                      onFocus={() => setShowTooltip(true)}
                      onBlur={() => setShowTooltip(false)}
                      onTouchEnd={() => setShowTooltip(!showTooltip)}
                      tabIndex={0}
                      role="button"
                      aria-label="ヘルプ情報を表示"
                    >
                      ?
                    </div>
                    {showTooltip && (
                      <div style={styles.tooltip}>
                        <p>
                          ご希望の検索結果が出てこない場合は、こちらのフォームからご依頼ください
                          <br />
                          <span data-wovn-ignore>{`（手数料: ${dig(
                            store,
                            'user',
                            'organization',
                            'arrangement_request_setting',
                            'fee'
                          )}）`}</span>
                          円
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}
              <SelectDefaultArea isOrganization show={store.currentTab === 'none'} serviceId={serviceId} sp />
            </ContentBody>
          )}
          {/* 旧コメント: SelectListAreaShortdistance は hidden 状態でレンダリングされるようにしておく */}
          <div style={styles.searchShortDistanceArea(store.currentTab !== 'none')}>
            <div style={styles.backHeader}>
              <Link onClick={() => store.handleClickTab('none')} style={styles.backHeaderLink}>
                {'< 戻る'}
              </Link>
            </div>
            <ContentBody style={styles.body}>
              {store.result.items.map((item, i) => (
                <ListAreaShortDistance
                  key={item.index}
                  icon={String(i + 1)}
                  repository={repository}
                  store={store}
                  hotelPriceLimit={item.hotelPriceLimit}
                  listWrapper={item.elementList}
                  type={item.elementType()}
                  subtype={item.flightIndex}
                  show={store.result.type === 'separate' && store.currentTab === item.index}
                  queryItem={query.items[i]}
                  resultItem={item}
                  query={query}
                  serviceId={serviceId}
                  showSearchBox={showSearchBox}
                  toggleShowSearchBox={toggleShowSearchBox}
                />
              ))}
              {store.result.packageItems[store.result.airline].map((item, i) => (
                <ListAreaShortDistance
                  key={item.index}
                  hotelPriceLimit={item.hotelPriceLimit}
                  icon={String(i + 1)}
                  repository={repository}
                  store={store}
                  listWrapper={item.elementList}
                  type={item.elementType()}
                  subtype={item.flightIndex}
                  queryItem={query.items[i]}
                  resultItem={item}
                  query={query}
                  serviceId={serviceId}
                  show={store.result.type === 'airPackage' && store.currentTab === item.index}
                  direction={i === 0 ? 'out' : i === 2 ? 'home' : undefined}
                  showSearchBox={showSearchBox}
                  toggleShowSearchBox={toggleShowSearchBox}
                />
              ))}
              {store.result.jrPackageItems.map((item, i) => (
                <ListAreaShortDistance
                  key={item.index}
                  hotelPriceLimit={item.hotelPriceLimit}
                  icon={String(i + 1)}
                  repository={repository}
                  store={store}
                  listWrapper={item.elementList}
                  type={item.elementType()}
                  subtype={item.flightIndex}
                  queryItem={query.items[i]}
                  resultItem={item}
                  query={query}
                  serviceId={serviceId}
                  show={store.result.type === 'jrPackage' && store.currentTab === item.index}
                  direction={i === 0 ? 'out' : i === 2 ? 'home' : undefined}
                  showSearchBox={showSearchBox}
                  toggleShowSearchBox={toggleShowSearchBox}
                />
              ))}
            </ContentBody>
          </div>
        </>
      )}
    </div>
  );
};

function getStyles(theme: AITTheme) {
  return {
    backHeader: {
      background: theme.backHeaderBgColor ?? '#292929',
      padding: '10px'
    } as React.CSSProperties,
    backHeaderLink: {
      color: getColor('brand', 'primary')({ theme })
    } as React.CSSProperties,
    body: {
      padding: 0
    } as React.CSSProperties,
    requestText: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '-90px',
      paddingLeft: '22px'
    } as React.CSSProperties,
    requestFormLink: {
      borderBottom: '1px solid',
      cursor: 'pointer'
    } as React.CSSProperties,
    tooltipContainer: {
      position: 'relative'
    } as React.CSSProperties,
    helpButton: {
      background: '#6c757d',
      color: 'white',
      fontSize: '11px',
      lineHeight: '13px',
      width: '15px',
      height: '15px',
      borderRadius: '8px',
      textAlign: 'center' as const,
      cursor: 'help',
      margin: 'auto',
      marginLeft: '2px'
    } as React.CSSProperties,
    tooltip: {
      position: 'absolute',
      top: '110%',
      right: '-60px',
      minWidth: '200px',
      background: '#f8f9fa',
      border: '1px solid #6c757d',
      borderRadius: '3px',
      padding: '5px',
      zIndex: 10,
      color: '#333',
      textAlign: 'left' as const,
      fontWeight: 'normal'
    } as React.CSSProperties,
    searchShortDistanceArea: (isOpen: boolean) =>
      ({
        display: isOpen ? 'block' : 'none'
      } as React.CSSProperties)
  } as const;
}

export default SpShortDistance;
